import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import type { DailyCheckInAnalysisRef } from "@/components/DailyCheckInAnalysisClean";
import type { CheckInAnalysis } from "../types";

interface UseAnalysisSyncReturn {
	analysisRef: React.RefObject<DailyCheckInAnalysisRef>;
	analysisData: CheckInAnalysis | null;
}

export function useAnalysisSync(): UseAnalysisSyncReturn {
	const [searchParams, setSearchParams] = useSearchParams();
	const analysisRef = useRef<DailyCheckInAnalysisRef>(null);
	const [analysisData, setAnalysisData] = useState<CheckInAnalysis | null>(
		null,
	);

	// Handle URL parameter triggers
	useEffect(() => {
		const refreshAnalysis = searchParams.get("refreshAnalysis");
		const triggerPending = searchParams.get("triggerPending");

		if (refreshAnalysis === "true" && analysisRef.current) {
			console.log("🔄 HomePage: Refreshing analysis due to URL parameter");
			analysisRef.current.refresh();
			setSearchParams((params) => {
				params.delete("refreshAnalysis");
				return params;
			});
		}

		if (triggerPending === "true" && analysisRef.current) {
			console.log("🔄 HomePage: Triggering pending state for new check-in");
			analysisRef.current.showPending();
			setSearchParams((params) => {
				params.delete("triggerPending");
				return params;
			});
		}
	}, [searchParams, setSearchParams]);

	// Sync analysis data from component
	useEffect(() => {
		const interval = setInterval(() => {
			if (analysisRef.current) {
				const currentAnalysis = analysisRef.current.getAnalysis();
				if (currentAnalysis !== analysisData) {
					setAnalysisData(currentAnalysis);
				}
			}
		}, 1000);

		return () => clearInterval(interval);
	}, [analysisData]);

	return {
		analysisRef,
		analysisData,
	};
}
